import type { QueryClient } from "@tanstack/react-query";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionOverrideReview,
  IAiSpreadsheetResponse,
  IAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import { aiScriptSpreadsheetKey } from "./keys";

export const patchAiSpreadsheetRegionCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  update: (regions: IAiSpreadsheetRegion[]) => IAiSpreadsheetRegion[],
) => {
  queryClient.setQueryData<IAiSpreadsheetResponse>(
    aiScriptSpreadsheetKey(businessId, runId),
    (data) =>
      data
        ? {
            ...data,
            spreadsheet: {
              ...data.spreadsheet,
              sheets: data.spreadsheet.sheets.map((sheet) =>
                sheet.id === sheetId
                  ? { ...sheet, regions: update(sheet.regions ?? []) }
                  : sheet,
              ),
            },
          }
        : data,
  );
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    (data) =>
      data
        ? {
            ...data,
            sheet: {
              ...data.sheet,
              regions: update(data.sheet.regions ?? []),
            },
          }
        : data,
  );
};

export const invalidateAiSpreadsheetRegionCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: aiScriptSpreadsheetKey(businessId, runId),
    }),
    queryClient.invalidateQueries({
      queryKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    }),
  ]);

export const patchAiSpreadsheetRegionOverrideReviewCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  update: (
    reviews: IAiSpreadsheetRegionOverrideReview[],
  ) => IAiSpreadsheetRegionOverrideReview[],
) => {
  queryClient.setQueryData<IAiSpreadsheetResponse>(
    aiScriptSpreadsheetKey(businessId, runId),
    (data) =>
      data
        ? {
            ...data,
            spreadsheet: {
              ...data.spreadsheet,
              region_override_reviews: update(
                data.spreadsheet.region_override_reviews ?? [],
              ),
              sheets: data.spreadsheet.sheets.map((sheet) =>
                sheet.id === sheetId
                  ? {
                      ...sheet,
                      region_override_reviews: update(
                        sheet.region_override_reviews ?? [],
                      ),
                    }
                  : sheet,
              ),
            },
          }
        : data,
  );
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    (data) =>
      data
        ? {
            ...data,
            sheet: {
              ...data.sheet,
              region_override_reviews: update(
                data.sheet.region_override_reviews ?? [],
              ),
            },
          }
        : data,
  );
};

export const syncConfirmedAiSpreadsheetRegionOverrideCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  overrideId: string,
  region: IAiSpreadsheetRegion | null,
) => {
  patchAiSpreadsheetRegionOverrideReviewCaches(
    queryClient,
    businessId,
    runId,
    sheetId,
    (reviews) => reviews.filter((review) => review.id !== overrideId),
  );

  if (region) {
    patchAiSpreadsheetRegionCaches(
      queryClient,
      businessId,
      runId,
      sheetId,
      (regions) => [
        ...regions.filter((current) => current.id !== region.id),
        region,
      ],
    );
    return Promise.resolve();
  }

  return invalidateAiSpreadsheetRegionCaches(
    queryClient,
    businessId,
    runId,
    sheetId,
  );
};
